import { IonCard, IonCardContent, IonIcon, IonImg, IonText } from '@ionic/react';
import { checkmarkDoneOutline, ellipsisVertical } from 'ionicons/icons';
import { useState } from 'react';
import { useHistory } from 'react-router';

import { ActionSheetReportCard } from '@/components/Actionsheets';
import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Tag } from '@/components/Tag/Tag';
import { toast } from '@/components/Toaster/Toast';

import { getTitleAndDescriptionFromReportInfo } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useReportContext } from '@/contexts/reportContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import Running from '@/assets/icons/Running.png';
import SLSquatter from '@/assets/icons/SingleLegSquat.png';
import Squatter from '@/assets/icons/Squat.png';
import Walking from '@/assets/icons/Walking.png';
import ReportCardPattern from '@/assets/report-card-pattern.svg';

import { ExportedClient, ReportInfo, ReportStatus, ReportType } from '@/models';

import styles from './ReportListCard.module.css';

import type { ITag } from '@/validations/tag';

interface ReportListCardProps {
  id: string;
  name: string;
  date: Date;
  reportType: ReportType;
  majorVersion: number;
  tags: ITag[];
  reportInfo: ReportInfo;
}

export const ReportListCard = ({ id, name, date, reportType, majorVersion, tags, reportInfo }: ReportListCardProps) => {
  const { title, description } = getTitleAndDescriptionFromReportInfo(reportInfo);
  const history = useHistory();
  const pending = reportInfo.status === ReportStatus.PENDING;
  const error =
    reportInfo.reportType === ReportType.NOTENOUGHDATA ||
    reportInfo.status === ReportStatus.ERROR ||
    reportInfo.reportType === ReportType.ERROR ||
    reportInfo.reportType === ReportType.KNEEL ||
    reportInfo.reportType === ReportType.KNEER;

  const { doDelete } = useReportContext();
  const [showActionSheet, setShowActionSheet] = useState(false);

  const imageMap = new Map([
    [ReportType.WALK, Walking],
    [ReportType.RUN, Running],
    [ReportType.SQUAT, Squatter],
    [ReportType.SLSQUAT, SLSquatter],
  ]);

  const activityNamesMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'Single Leg Squat'],
  ]);

  const routeSegmentMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'SingleLegSquat'],
  ]);

  const handleClick = () => {
    if (routeSegmentMap.has(reportType)) {
      history.push(`/${majorVersion === 1 ? 'reportsV1' : 'reportsV2'}/${routeSegmentMap.get(reportType)}/${id}`, {
        title,
        description,
      });
    }
  };

  return (
    <>
      <ActionSheetReportCard
        doDelete={doDelete}
        isOpen={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        reportInfo={reportInfo}
        showAssignToSession={true}
        showDeleteError={() =>
          toast({
            variant: 'error',
            title: 'Error deleting file',
            description: `No report ID for the given report ${reportInfo.filename}.`,
          })
        }
        showPDFError={() =>
          toast({
            variant: 'error',
            title: 'Error downloading file',
            description: `Can't download PDF for ${reportInfo.filename}, please contact support.`,
          })
        }
        // onGeneratePDF={handleGeneratePDF}
      />

      <IonCard
        className={mergeClassNames(styles.card, error && styles.error)}
        onClick={!error && !pending ? handleClick : undefined}>
        {pending ? (
          <div className={styles.pending}>
            <CustomLoading message={'Creating report...'} size='small' />
          </div>
        ) : (
          <>
            <div className={styles.color}>
              <IonImg className={styles.icon} src={imageMap.get(reportType) ?? Walking} />

              <IonImg className={styles.pattern} src={ReportCardPattern} />
            </div>

            <IonCardContent className={styles.content}>
              <div className={styles.nameWrapper}>
                {activityNamesMap.get(reportType) ? (
                  <IonText className={styles.activity}>{activityNamesMap.get(reportType)}</IonText>
                ) : (
                  <>
                    {error && (
                      <div className={styles.errorMessage}>
                        <IonText className={styles.errorText}>
                          {reportInfo.status === ReportStatus.ERROR &&
                            `Sorry, we couldn't process your data. Please contact support.`}
                          {reportInfo.reportType === ReportType.ERROR &&
                            `Sorry, we couldn't process your data. Please contact support.`}
                          {reportInfo.reportType === ReportType.KNEEL &&
                            `Left Thigh and Calf Sensors seem to be swapped.`}
                          {reportInfo.reportType === ReportType.KNEER &&
                            `Right Thigh and Calf Sensors seem to be swapped.`}
                          {reportInfo.reportType === ReportType.NOTENOUGHDATA && `Not enough data to generate report.`}
                        </IonText>
                      </div>
                    )}
                  </>
                )}

                <div className={styles.actions}>
                  {reportInfo.isAssigned && <IonIcon color='success' icon={checkmarkDoneOutline} />}
                  <div
                    aria-label='More actions'
                    className={styles.moreAction}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowActionSheet(true);
                    }}
                    role='button'
                    tabIndex={0}>
                    <Icon className={styles.chevron} color='#829696' name='menu' size={14} />
                  </div>
                  <Icon
                    className={styles.chevron}
                    color='#829696'
                    direction={IconRotate.East}
                    name='chevron'
                    size={14}
                  />
                </div>
              </div>

              <div className={styles.header}>
                <>
                  <IonText className={styles.name}>{name}</IonText>
                  <IonText class={styles.date}>
                    <span className={styles.dateString}>
                      {date.toLocaleDateString(undefined, {
                        month: 'short',
                        year: 'numeric',
                        day: 'numeric',
                      })}
                    </span>
                    {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  </IonText>
                </>
              </div>

              {tags.length > 0 && (
                <div className={styles.tags}>
                  <>
                    {tags
                      .filter((tag) => tag.id !== '')
                      .map((tag) => (
                        <Tag key={tag.id} variant='measurement'>
                          {tag.label}
                        </Tag>
                      ))}
                  </>
                </div>
              )}

              {error && !!activityNamesMap.get(reportType) && (
                <div className={styles.errorMessage}>
                  <IonText className={styles.errorText}>
                    {reportInfo.status === ReportStatus.ERROR &&
                      `Sorry, we couldn't process your data. Please contact support.`}
                    {reportInfo.reportType === ReportType.ERROR &&
                      `Sorry, we couldn't process your data. Please contact support.`}
                    {reportInfo.reportType === ReportType.KNEEL && `Left Thigh and Calf Sensors seem to be swapped.`}
                    {reportInfo.reportType === ReportType.KNEER && `Right Thigh and Calf Sensors seem to be swapped.`}
                  </IonText>
                </div>
              )}
            </IonCardContent>
          </>
        )}
      </IonCard>
    </>
  );
};
